import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { CALLS_INCLUDED, CALL_COLUMNS, callDraftRow, callLine, fromCallRow, type Call, type CallChange, type CallDraft, type CallRow } from "../lib/calls";

/* THE COACH'S SCHEDULED CALLS — THE ONE READ AND THE ONE WRITE PATH ON THE WEB (his idea 2026-10-10, `docs/CALLS-BRIEF.md`).
 *
 * ⚠️ EVERY `.from("coach_calls")` IN THE WEB APP IS IN THIS FILE (gate `calls-are-booked-and-rung` counts them): the Dashboard
 * card and a client's page both read through `useCalls` and write through its `write` — book, move, cancel.
 *
 * ⚠️ EVERY WRITE LEAVES ONE CHAT LINE (`callLine`), sent as the coach into the pair's thread and announced with the same
 * fire-and-forget push the Inbox sends — so the client hears of it and the chat stays the record. A line that fails is not
 * the call failing: the call is saved and the window closes (a refusal would invite a second Save — a second booking). After every write the hook re-reads and says so on
 * `window` (`ht:calls`), so a card and a page mounted at once show one answer.
 *
 * "MINE" IS EXPLICIT (`.eq("coach_id", coachId)`): the read policy also hands a coach the calls where HE is the client. */
export const CALLS_CHANGED = "ht:calls";

export type CallsState =
  | { status: "off" }
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; calls: Call[]; names: Record<string, string> };

export type CallWrite = {
  book: (clientId: string, d: CallDraft) => Promise<string | null>;
  move: (c: Call, d: CallDraft) => Promise<string | null>;
  cancel: (c: Call) => Promise<string | null>;
  /** The call is over: its ONE line (`Call · 32 min`, `callEndLine`), the same poster as book · move · cancel. */
  ended: (c: Call, line: string) => Promise<void>;
};

export function useCalls(coachId: string, zone: string): { state: CallsState; write: CallWrite } {
  const [state, setState] = useState<CallsState>(CALLS_INCLUDED ? { status: "loading" } : { status: "off" });
  const [seq, setSeq] = useState(0);

  useEffect(() => {
    const bump = () => setSeq((n) => n + 1);
    window.addEventListener(CALLS_CHANGED, bump);
    return () => window.removeEventListener(CALLS_CHANGED, bump);
  }, []);

  useEffect(() => {
    if (!CALLS_INCLUDED) return;
    let cancelled = false;
    void (async () => {
      // An hour back, so a running call (up to 60 min) still shows its Join.
      const since = new Date(Date.now() - 3_600_000).toISOString();
      const rows = await supabase.from("coach_calls").select(CALL_COLUMNS)
        .eq("coach_id", coachId).is("cancelled_at", null).gte("starts_at", since)
        .order("starts_at", { ascending: true });
      if (rows.error !== null) {
        if (!cancelled) setState({ status: "error", message: rows.error.message });
        return;
      }
      const calls = (rows.data as CallRow[]).map(fromCallRow);
      const ids = [...new Set(calls.map((c) => c.clientId))];
      const people = ids.length === 0 ? { data: [] as { id: string; first_name: string | null; last_name: string | null }[] }
        : await supabase.from("profiles").select("id, first_name, last_name").in("id", ids);
      const names: Record<string, string> = {};
      for (const id of ids) {
        const p = (people.data ?? []).find((x) => x.id === id);
        const full = [p?.first_name, p?.last_name].filter((s) => s !== null && s !== undefined && s !== "").join(" ");
        names[id] = full === "" ? "This client" : full;
      }
      if (!cancelled) setState({ status: "ready", calls, names });
    })();
    return () => { cancelled = true; };
  }, [coachId, seq]);

  /** The ONE poster: a line, as the coach, into the pair's thread — and its push. Null when it landed. */
  const post = useCallback(async (clientId: string, body: string): Promise<string | null> => {
    const { data, error } = await supabase.from("coach_messages")
      .insert({ coach_id: coachId, client_id: clientId, sender_id: coachId, body })
      .select("id").single();
    if (error !== null || data === null) return error?.message ?? "no row came back";
    void supabase.functions.invoke("send-message-push", { body: { messageId: data.id } }).catch(() => undefined);
    return null;
  }, [coachId]);

  /** A change's chat line. */
  const line = useCallback((change: CallChange, c: Pick<Call, "clientId" | "startsAt" | "minutes" | "topic">): Promise<string | null> =>
    post(c.clientId, callLine(change, c, zone)), [post, zone]);

  const finish = useCallback(async (change: CallChange, c: Pick<Call, "clientId" | "startsAt" | "minutes" | "topic">): Promise<string | null> => {
    const lineFailure = await line(change, c);
    window.dispatchEvent(new Event(CALLS_CHANGED));
    // The call IS saved: a refusal here would invite a second Save (a second booking). Said in the console, as the phone logs it.
    if (lineFailure !== null) console.warn(`[calls] saved; the chat line did not send: ${lineFailure}`);
    return null;
  }, [line]);

  const write: CallWrite = {
    book: async (clientId, d) => {
      const row = callDraftRow(d, new Date(), zone);
      if (row === null) return "That call is not complete.";
      const { data, error } = await supabase.from("coach_calls")
        .insert({ coach_id: coachId, client_id: clientId, ...row }).select(CALL_COLUMNS).single();
      if (error !== null || data === null) return `That call did not save: ${error?.message ?? "no row came back"}`;
      return finish("booked", fromCallRow(data as CallRow));
    },
    move: async (c, d) => {
      const row = callDraftRow(d, new Date(), zone);
      if (row === null) return "That call is not complete.";
      const { data, error } = await supabase.from("coach_calls")
        .update(row).eq("id", c.id).eq("coach_id", coachId).select(CALL_COLUMNS).single();
      if (error !== null || data === null) return `That call did not save: ${error?.message ?? "no row came back"}`;
      return finish("moved", fromCallRow(data as CallRow));
    },
    cancel: async (c) => {
      const { error } = await supabase.from("coach_calls")
        .update({ cancelled_at: new Date().toISOString() }).eq("id", c.id).eq("coach_id", coachId);
      if (error !== null) return `That call was not cancelled: ${error.message}`;
      return finish("cancelled", c);
    },
    ended: async (c, endLine) => {
      const failure = await post(c.clientId, endLine);
      if (failure !== null) console.warn(`[calls] the call's line did not send: ${failure}`);
    },
  };

  return { state, write };
}
